import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { CartItem } from '../helpers/rental_plan'
import CurrencyUSD from '../misc/currency_formats'
import SaleRatings from './sale_ratings'

import styles from '../../assets/stylesheets/pages/cart/items.module.scss'
import { SelectInput, NumberInput, Checkbox, IconButton, Alert } from 'lensrentals-components'

const Item = props => {
  const {
    type,
    item: {
      id,
      productUrl,
      imageUrl,
      name,
      getProtection = () => null,
      quantity,
      waiver,
      warranty,
      hasWarranty,
      productPrice,
      noLongerAvailable,
      availabilityLabel,
      discontinued,
      quantityAvailable,
      overallRating = null,
      simpleRatings = null,
      missingItems = [],
      extraItems = [],
      correctionMessage
    },
    removeItem,
    updateQuantity,
    updateWaiver,
    updateWarranty,
    toggleLenscapModal,
    onInputFocus,
    onInputBlur
  } = props

  const protection = getProtection()

  const insuranceOptions = [
    { name: 'No Lenscap Protection Plan', value: 'none' },
    { name: 'Lenscap: Damage Only', value: 'LENSCAP' },
    { name: 'Lenscap+: Damage & Theft', value: 'LENSCAP_PLUS' }
  ]

  const [itemCorrectionMessage, setItemCorrectionMessage] = useState(null)

  useEffect(() => {
    // grab message out of props and cache it here so it can persist client side when the server returns a new cartPlan
    if (correctionMessage) {
      setItemCorrectionMessage(correctionMessage)
    }
  }, [correctionMessage])

  return (
    <li
      id={`cart_item_${id}`}
      className={cx(
        'cart-item-row',
        `cart-item-row--${type}`,
        styles.item__wrapper)}
    >
      <article>
        <div className={styles.item}>
          <input name='cart[cart_items][][id]' type='hidden' value={id} />
          <div className={styles.item__info}>
            <a href={productUrl}>
              <img
                src={imageUrl}
                alt={name}
                className={styles['item__info-thumbnail']}
              />
            </a>
            <div className={styles['item__info-text']}>
              <a
                className={cx(
                  'name',
                  styles.item__name,
                  (availabilityLabel || discontinued) && styles['item__name--unavailable']
                )}
                href={productUrl}
              >
                {name}
                {type === 'sale' && (overallRating && simpleRatings) &&
                  <SaleRatings
                    overallRating={overallRating}
                    simpleRatings={simpleRatings}
                  />}
              </a>
              {availabilityLabel &&
                <span className={cx('later', styles['item__availability-label'])}>
                  {availabilityLabel}
                </span>}
              {discontinued &&
                <span className={cx('later', styles['item__availability-label'])}>
                  <strong>Unavailable to Rent</strong>
                </span>}
              {type === 'sale' && !!missingItems.length &&
                <details className={styles['item__missing-items-warning']}>
                  <summary>Items Not Included</summary>
                  <ul>
                    {missingItems.map((item, index) => {
                      return <li key={index}>{item.fullName}</li>
                    })}
                  </ul>
                </details>}
              {type === 'sale' && !!extraItems.length &&
                <details className={styles['item__extra-items-warning']}>
                  <summary>Extra Items Included</summary>
                  <ul>
                    {extraItems.map((item, index) => {
                      return <li key={index}>{item.fullName}</li>
                    })}
                  </ul>
                </details>}
              {type === 'rental' &&
                <div className={styles.item__lenscap}>
                  <SelectInput
                    id={`lenscap-${id}`}
                    className={cx('waiver_type', styles['lenscap-select'])}
                    name={`lenscap-${id}`}
                    placeholder='Choose Protection:'
                    options={insuranceOptions}
                    value={protection || ''}
                    onChange={updateWaiver(id)}
                  />
                  {/* fields with correct names to post to checkout controller */}
                  <input className='waiver_type_value' type='hidden' name='cart[cart_items][][waiver][type]' value={waiver.type} />
                  <input className='waiver_quantity' type='hidden' name='cart[cart_items][][waiver][quantity]' value={waiver.quantity} />
                  <IconButton
                    icon='QuestionMark'
                    title='Lenscap Information'
                    className={styles['item__lenscap-info-button']}
                    color='highlight'
                    onClick={toggleLenscapModal}
                    type='button'
                  />
                </div>
              }
              {type === 'sale' && warranty?.name &&
                <>
                  <Checkbox
                    className={cx('sale-warranty', styles.item__warranty)}
                    id={`warranty-${id}`}
                    name={`warranty-${id}`}
                    label={warranty.name}
                    value={`${hasWarranty}`}
                    checked={hasWarranty}
                    onChange={updateWarranty(id)}
                  />
                  <input className='waiver_type_value' type='hidden' name='cart[cart_items][][hasWarranty]' value={hasWarranty} />
                </>}
            </div>
          </div>

          <div className={styles.item__quantity}>
            <NumberInput
              id={`quantity-${id}`}
              className={cx('quantity', 'quantity-field', styles['item__quantity-input'])}
              name='cart[cart_items][][quantity]'
              label='Qty:'
              value={quantity}
              placeholder='0'
              min='0'
              max={quantityAvailable || '100'}
              onChange={updateQuantity(id)}
              onFocus={onInputFocus}
              onBlur={onInputBlur(id)}
              onMouseDown={(e) => e.target.focus()}
              onKeyPress={(e) => { e.key === 'Enter' && e.preventDefault() }}
              disabled={noLongerAvailable}
            />
          </div>

          <div className={styles.item__price}>
            <span className={styles['item__price-detail']}>
              <span className={styles['item__price-label']}>
                Price:
              </span>
              <span className='price-quantity'>
                <CurrencyUSD amount={productPrice} />
              </span>
            </span>

            {protection &&
              <span className={styles['item__price-detail']}>
                <span className={styles['item__price-label']}>
                  Coverage:
                </span>
                <span className='waiver-price'>
                  <CurrencyUSD amount={waiver.priceForType} />
                </span>
              </span>}

            {warranty &&
              <span className={styles['item__price-detail']}>
                <span className={styles['item__price-label']}>
                  Warranty:
                </span>
                <span className='warranty-price'>
                  <CurrencyUSD amount={hasWarranty ? warranty.price : 0} />
                </span>
              </span>}

            <IconButton
              className={cx('remove', styles['item__remove-button'])}
              title='Remove Item From Cart'
              icon='X'
              color='danger'
              onClick={removeItem(id)}
              type='button'
            />
          </div>
        </div>

        {!!itemCorrectionMessage &&
          <Alert
            className={cx('error', styles.item__alert)}
            show={!!itemCorrectionMessage}
            onClose={() => setItemCorrectionMessage(null)}
            selfDestruct
            color='danger'
          >
            <span>{itemCorrectionMessage}</span>
          </Alert>}
      </article>
    </li>
  )
}

Item.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.instanceOf(CartItem),
    PropTypes.object
  ]),
  removeItem: PropTypes.func.isRequired,
  updateQuantity: PropTypes.func.isRequired,
  updateWaiver: PropTypes.func,
  updateWarranty: PropTypes.func,
  toggleLenscapModal: PropTypes.func.isRequired,
  onInputFocus: PropTypes.func.isRequired,
  onInputBlur: PropTypes.func.isRequired
}

export default Item
